import type { Form, FormField } from '../interfaces/form.interface';
import { FormType } from '../interfaces/form.interface';
import { DropdownData, InputType } from '../interfaces/input.interface';
import { ReactiveDropdownOption, ReactiveFormValueUtils } from './reactive-form-value.utils';

export type ReactiveFormDefaultValueContext<T> = {
    form: Form<T>;
    previousData: T | null;
    externalDropdownsReady: boolean;
};

export class ReactiveFormDefaultValueResolver<T> {
    public resolve(field: FormField<T>, context: ReactiveFormDefaultValueContext<T>): unknown {
        if (ReactiveFormValueUtils.isRepeatableField(field)) {
            return [];
        }

        const isMulti = ReactiveFormValueUtils.isMultiSelectField(field);

        if (context.form.type === FormType.DUPLICATION && field.conditions.isRememberedOnDuplicate === false) {
            return isMulti ? [] : '';
        }

        const previousValue = this.getPreviousValue(field, context.previousData);
        const hasPreviousValue = previousValue !== undefined &&
            previousValue !== null &&
            !(typeof previousValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(previousValue));

        if (hasPreviousValue) {
            return this.resolveKnownValue(field, previousValue, isMulti, context.externalDropdownsReady);
        }

        if (field.input?.defaultValue === null || field.input?.defaultValue === undefined) {
            return isMulti ? [] : '';
        }

        return this.resolveKnownValue(field, field.input.defaultValue, isMulti, context.externalDropdownsReady);
    }

    private getPreviousValue(field: FormField<T>, previousData: T | null): unknown {
        const record = previousData as Record<string, unknown> | null;

        if (!record) {
            return undefined;
        }

        const key = field.name ?? '';
        const candidateKeys = this.getPreviousValueCandidateKeys(key);

        for (const candidateKey of candidateKeys) {
            const value = record[candidateKey];

            if (ReactiveFormValueUtils.hasNonEmptyValue(value)) {
                return value;
            }
        }

        return candidateKeys.length > 0 ? record[candidateKeys[0] ?? key] : undefined;
    }

    private getPreviousValueCandidateKeys(key: string): string[] {
        const keys = new Set<string>();
        const normalizedKey = key.trim();

        if (normalizedKey !== '') {
            keys.add(normalizedKey);
        }

        if (normalizedKey.endsWith('_nombres')) {
            keys.add(`ids_${normalizedKey.replace(/_nombres$/, '')}`);
        }

        if (normalizedKey.startsWith('id_') && normalizedKey.length > 3) {
            const baseKey = normalizedKey.slice(3);

            keys.add(baseKey);
            keys.add(`nombre_${baseKey}`);
        }

        if (normalizedKey.startsWith('ids_') && normalizedKey.length > 4) {
            keys.add(`${normalizedKey.slice(4)}_nombres`);
            keys.add(normalizedKey.slice(4));
        }

        if (normalizedKey === 'tipo') {
            keys.add('tipo_usuario');
        }

        return Array.from(keys);
    }

    private resolveKnownValue(
        field: FormField<T>,
        rawValue: unknown,
        isMulti: boolean,
        externalDropdownsReady: boolean,
    ): unknown {
        if (isMulti) {
            return this.resolveMultiValue(field, rawValue, externalDropdownsReady);
        }

        if (field.input?.type === InputType.CALENDAR) {
            return this.resolveCalendarValue(field, rawValue);
        }

        if (field.input?.type === InputType.SWITCH) {
            return ReactiveFormValueUtils.normalizeSwitchValue(rawValue);
        }

        if (ReactiveFormValueUtils.isSingleDropdownField(field)) {
            return this.resolveSingleDropdownValue(field, rawValue, externalDropdownsReady);
        }

        if (typeof rawValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return '';
        }

        return rawValue;
    }

    private resolveCalendarValue(field: FormField<T>, rawValue: unknown): unknown {
        if (rawValue instanceof Date) {
            return this.formatDate(
                rawValue.getDate(),
                rawValue.getMonth() + 1,
                rawValue.getFullYear(),
                field,
            );
        }

        if (typeof rawValue !== 'string') {
            return rawValue;
        }

        const trimmedValue = rawValue.trim();

        if (ReactiveFormValueUtils.isBadDisplayToken(trimmedValue)) {
            return '';
        }

        const isoDate = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T].*)?$/.exec(trimmedValue);

        if (isoDate !== null) {
            return this.formatDate(Number(isoDate[3]), Number(isoDate[2]), Number(isoDate[1]), field);
        }

        const localDate = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(trimmedValue);

        if (localDate !== null) {
            return this.formatDate(Number(localDate[1]), Number(localDate[2]), Number(localDate[3]), field);
        }

        return rawValue;
    }

    private formatDate(day: number, month: number, year: number, field: FormField<T>): string {
        const dateFormat = field.input?.calendarConfiguration?.calendarDateFormat ?? 'dd/mm/yy';
        const separator = dateFormat.includes('-') ? '-' : '/';
        const dayValue = day.toString().padStart(2, '0');
        const monthValue = month.toString().padStart(2, '0');
        const yearValue = year.toString();
        const normalizedFormat = dateFormat.toLowerCase();

        if (normalizedFormat.startsWith('yy')) {
            return `${yearValue}${separator}${monthValue}${separator}${dayValue}`;
        }

        if (normalizedFormat.startsWith('mm')) {
            return `${monthValue}${separator}${dayValue}${separator}${yearValue}`;
        }

        return `${dayValue}${separator}${monthValue}${separator}${yearValue}`;
    }

    private resolveMultiValue(field: FormField<T>, rawValue: unknown, externalDropdownsReady: boolean): unknown[] {
        const items = this.parseMultiValue(rawValue);
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const looksLikeObjects = items.length > 0 && items.every((item) => {
            if (typeof item !== 'object' || item === null) {
                return false;
            }

            return ReactiveFormValueUtils.extractComparableId(item, primaryKey, identityKeys) !== null;
        });

        if (looksLikeObjects) {
            return items;
        }

        const ids = items
            .map((item) => ReactiveFormValueUtils.extractComparableId(item, primaryKey, identityKeys))
            .filter((item): item is string | number => item !== null);

        const options = this.getOptions(field);
        const mappedValues = ids
            .map((id) => ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, undefined, identityKeys))
            .filter((item) => item !== null);

        if (mappedValues.length > 0) {
            return mappedValues;
        }

        if (ids.length > 0) {
            return ids;
        }

        return externalDropdownsReady ? [] : ids;
    }

    private resolveSingleDropdownValue(field: FormField<T>, rawValue: unknown, externalDropdownsReady: boolean): unknown {
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);

        if (this.hasObjectPrimaryKey(rawValue, primaryKey, identityKeys)) {
            return rawValue;
        }

        if (typeof rawValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return '';
        }

        const id = ReactiveFormValueUtils.extractComparableId(rawValue, primaryKey, identityKeys);
        const mappedValue = id === null ? null : ReactiveFormValueUtils.mapIdToOptionValue(
            id,
            this.getOptions(field),
            primaryKey,
            undefined,
            identityKeys,
        );

        if (mappedValue !== null) {
            return mappedValue;
        }

        if (ReactiveFormValueUtils.hasNonEmptyValue(rawValue)) {
            return rawValue;
        }

        return externalDropdownsReady ? '' : rawValue;
    }

    private hasObjectPrimaryKey(value: unknown, primaryKey: string, identityKeys: readonly string[] = []): boolean {
        if (typeof value !== 'object' || value === null) {
            return false;
        }

        return ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys) !== null;
    }

    private parseMultiValue(rawValue: unknown): unknown[] {
        if (Array.isArray(rawValue)) {
            return rawValue;
        }

        if (typeof rawValue !== 'string' || rawValue.length === 0 || ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawValue) as unknown;

            if (Array.isArray(parsed)) {
                return parsed;
            }
        } catch {
            // Plain serialized ids are handled below.
        }

        return ReactiveFormValueUtils.splitSerializedIds(rawValue);
    }

    private getOptions(field: FormField<T>): ReactiveDropdownOption[] {
        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;

        return ReactiveFormValueUtils.getDropdownOptions(dropdownData);
    }
}
